import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import '@/components/tutorials/tutorialOverrides.css';
import { useTutorialStore } from '@/components/tutorials/useTutorialStore';
import { useProductIdStore } from '@/stores/useProductIdStore';
import {
  formatRecordCountPopoverDescription,
  type GuidedFilterPostApplyStepIndex,
} from './guidedFilterPostApplySteps';

const TUTORIAL_BLOCKING_LAYER_Z = 10001;

const FILTER_STEP: DriveStep = {
  element: '[data-tutorial="filter-pane"]',
  popover: {
    title: 'Filter added',
    description:
      'Your filter is now active in this panel (top-left of the dashboard). Charts and record lists below will show only matching records. You can edit FilterQL and category selections here anytime.',
    side: 'right',
    align: 'start',
  },
};

const SETTINGS_STEP: DriveStep = {
  element: '[data-tutorial="account-menu-combined-highlight"]',
  popover: {
    title: 'Resources & tutorials',
    description:
      'Open the Settings menu (gear icon), then choose Resources for the Filter Building Guide, dashboard tours, and other tutorials.',
    side: 'bottom',
    align: 'end',
  },
};

function buildRecordCountStep(): DriveStep {
  const { count, productIdsQueryDone, error } = useProductIdStore.getState();
  return {
    element: '[data-tutorial="record-count"]',
    popover: {
      title: 'Records matching your filter',
      description: formatRecordCountPopoverDescription(count, productIdsQueryDone, error),
      side: 'bottom',
      align: 'center',
    },
  };
}

function buildPostApplySteps(): DriveStep[] {
  return [FILTER_STEP, buildRecordCountStep(), SETTINGS_STEP];
}

export const GuidedFilterPostApplyTour: React.FC = () => {
  const location = useLocation();
  const driverRef = useRef<Driver | null>(null);
  const {
    guidedFilterPostApplyActive,
    guidedFilterPostApplyStep,
    setGuidedFilterPostApplyStep,
    endGuidedFilterPostApply,
  } = useTutorialStore();

  useEffect(() => {
    if (!guidedFilterPostApplyActive) {
      driverRef.current?.destroy();
      driverRef.current = null;
      return;
    }

    if (location.pathname !== '/dashboard') return;

    let cancelled = false;

    const waitForSelector = async (selector: string, timeoutMs = 8000) => {
      const deadline = Date.now() + timeoutMs;
      while (!cancelled && !document.querySelector(selector) && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 100));
      }
    };

    const waitForRecordCountQuery = async (timeoutMs = 12000) => {
      const { productIdsQueryDone, fetchProductMatchCount, error } =
        useProductIdStore.getState();
      if (!productIdsQueryDone && !error?.trim()) {
        void fetchProductMatchCount();
      }
      const deadline = Date.now() + timeoutMs;
      while (
        !cancelled &&
        !useProductIdStore.getState().productIdsQueryDone &&
        Date.now() < deadline
      ) {
        await new Promise((r) => setTimeout(r, 150));
      }
    };

    const runDriver = async () => {
      const step = guidedFilterPostApplyStep;
      await new Promise((r) => setTimeout(r, step === 2 ? 200 : 120));
      if (cancelled) return;

      if (step === 0) {
        await waitForSelector('[data-tutorial="filter-pane"]');
      } else if (step === 1) {
        await waitForSelector('[data-tutorial="record-count"]');
        await waitForRecordCountQuery();
      } else {
        await waitForSelector('[data-tutorial="account-menu-content"]');
        await waitForSelector('[data-tutorial="account-menu-combined-highlight"]');
      }
      if (cancelled) return;

      driverRef.current?.destroy();

      const steps = buildPostApplySteps();
      const lastIndex = steps.length - 1;

      const d = driver({
        showProgress: false,
        steps,
        nextBtnText: 'Next',
        doneBtnText: 'Done',
        allowClose: true,
        disableActiveInteraction: true,
        overlayClickBehavior: () => {},
        onDestroyed: () => {
          if (driverRef.current === d) driverRef.current = null;
        },
        onNextClick: (_element, _step, opts) => {
          const idx = opts.state.activeIndex ?? step;
          opts.driver.destroy();
          if (idx < lastIndex) {
            setGuidedFilterPostApplyStep((idx + 1) as GuidedFilterPostApplyStepIndex);
          } else {
            endGuidedFilterPostApply();
          }
        },
        onCloseClick: (_el, _s, closeOpts) => {
          closeOpts.driver.destroy();
          endGuidedFilterPostApply();
        },
      });

      driverRef.current = d;
      d.drive(step);
    };

    void runDriver();

    return () => {
      cancelled = true;
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, [
    guidedFilterPostApplyActive,
    guidedFilterPostApplyStep,
    location.pathname,
    setGuidedFilterPostApplyStep,
    endGuidedFilterPostApply,
  ]);

  if (!guidedFilterPostApplyActive || typeof document === 'undefined') return null;

  return createPortal(
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: TUTORIAL_BLOCKING_LAYER_Z,
        pointerEvents: 'auto',
        background: 'transparent',
      }}
    />,
    document.body
  );
};
